// Hardcoded Support Center content: KB articles, troubleshooting wizards,
// FAQs, video academy, maintenance checklists, contact constants.

export const SUPPORT_PHONE = "(336) 203-3184";
export const SUPPORT_PHONE_TEL = "tel:+13362033184";
export const SUPPORT_EMAIL = "support@neilai.ai";
export const SALES_PHONE = "(336) 962-4539";
export const SALES_PHONE_TEL = "tel:+13369624539";
export const ESCALATION_EMAIL = "neilai@neilaiglobalsolutionsllc.com";
export const SUPPORT_DIRECTOR = "Kenon Lattimore";
export const BOOKING_URL =
  "https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics";

export type WizardStep = {
  title: string;
  body: string;
  ok?: string; // text shown for "this fixed it"
  next?: string; // text shown for "still broken"
};

export type Wizard = {
  slug: string;
  robot: string;
  issue: string;
  category: "robotics" | "ai";
  steps: WizardStep[];
  escalate: string; // what to tell the user if they exhaust steps
};

export const WIZARDS: Wizard[] = [
  // ---- Pudu Robots ----
  {
    slug: "cc1-pro-wont-start",
    robot: "CC1 Pro",
    issue: "Robot won't start",
    category: "robotics",
    steps: [
      { title: "Check the emergency stop", body: "Make sure the red emergency-stop button on top of the robot is released (twist it clockwise until it pops back up)." },
      { title: "Verify battery level", body: "Dock the robot and let it charge for at least 30 minutes. The dock light should be solid or flashing." },
      { title: "Check charging dock contact", body: "Confirm the robot is flush against the dock. Wipe the charging pins on the robot and the dock with a dry cloth." },
      { title: "Hold the power button", body: "Press and hold the power button for a full 3 seconds, then release. A startup chime should play." },
      { title: "Restart the system", body: "If you still see no response, power off, wait 10 seconds, then power on again." },
    ],
    escalate:
      "If the robot still won't start, this may be a battery or hardware issue. Open a ticket below — we'll dispatch a certified technician.",
  },
  {
    slug: "cc1-pro-wont-dock",
    robot: "CC1 Pro",
    issue: "Robot won't return to dock",
    category: "robotics",
    steps: [
      { title: "Clear the dock area", body: "Make sure the 2-meter zone in front of the dock is free of chairs, boxes, signs, or cables." },
      { title: "Confirm the dock has power", body: "Check that the dock's LED is on — green or flashing means it's ready to receive the robot." },
      { title: "Wipe the navigation sensors", body: "Use a dry microfiber cloth to clean the LiDAR and front-facing sensors on the robot." },
      { title: "Manually dock once", body: "Push the robot onto the dock until you hear a click. Confirm the charging light comes on within 10 seconds." },
    ],
    escalate:
      "Persistent docking issues usually mean a remap is needed. Open a service request and we'll schedule a remote or onsite remap.",
  },
  {
    slug: "cc1-pro-poor-cleaning",
    robot: "CC1 Pro",
    issue: "Poor cleaning performance",
    category: "robotics",
    steps: [
      { title: "Inspect the brushes", body: "Remove the side brushes and check for hair, string, or debris. Clear any wrap-around." },
      { title: "Fill the clean-water tank", body: "Make sure the clean-water tank is full and the dirty-water tank is empty." },
      { title: "Check the pads or rollers", body: "Confirm the cleaning pads aren't caked with debris — rinse if needed." },
      { title: "Verify cleaning mode", body: "Open the app or the robot's screen and confirm the correct cleaning mode is selected for the floor type." },
    ],
    escalate:
      "If cleaning quality doesn't improve, the brushes/pads may need replacement. Open a ticket and we'll ship parts or schedule service.",
  },
  {
    slug: "mt1-max-mapping",
    robot: "MT1 Max",
    issue: "Mapping or navigation issues",
    category: "robotics",
    steps: [
      { title: "Note the error", body: "Check the robot's screen for an error code or 'lost position' alert and write it down." },
      { title: "Move robot to a known location", body: "Push the robot back to its charging dock or to the nearest wall marker — it should re-localize automatically." },
      { title: "Restart the robot", body: "Power off, wait 10 seconds, power back on." },
      { title: "Check for new obstacles", body: "Has the layout changed? New furniture, signage, or stacked pallets often confuse the map." },
    ],
    escalate:
      "If the issue continues, the map needs a refresh. Submit a service request — type 'Mapping' — and we'll schedule a remap.",
  },
  {
    slug: "t600-any",
    robot: "T600",
    issue: "Any T600 issue",
    category: "robotics",
    steps: [
      { title: "Stop and secure", body: "If safe, press the emergency stop. Move staff away from the robot's travel path." },
    ],
    escalate:
      "All T600 and T300 industrial robot issues are handled by a certified technician — please call our 24/7 line immediately.",
  },
  {
    slug: "bellabot-pro-delivery",
    robot: "BellaBot Pro",
    issue: "Table delivery or navigation issues",
    category: "robotics",
    steps: [
      { title: "Confirm the table area is clear", body: "Check for chairs, high-chairs, or signage blocking the route to the table." },
      { title: "Check the saved table location", body: "In the app, confirm the table still has the correct saved name and position." },
      { title: "Restart the robot", body: "Power off, wait 10 seconds, power back on." },
      { title: "Test a known-good table", body: "Send the robot to a table that's worked before — this isolates whether the issue is one table or the whole map." },
    ],
    escalate:
      "If only one table is affected the location may need re-saving; if multiple tables are affected we should remap. Open a ticket.",
  },
  {
    slug: "kettybot-pro-display",
    robot: "KettyBot Pro",
    issue: "Display or advertising issues",
    category: "robotics",
    steps: [
      { title: "Confirm internet connection", body: "KettyBot needs 2.4 GHz WiFi to refresh ads. Verify the robot is online in the app." },
      { title: "Restart the robot", body: "Power off, wait 10 seconds, power back on. Ads should re-sync within 2 minutes." },
      { title: "Check ad content in the dashboard", body: "Confirm your ad campaign is set to 'active' in the management dashboard." },
    ],
    escalate:
      "Persistent display issues usually need a firmware reset. Open a ticket and we'll handle it remotely.",
  },
  {
    slug: "d9-startup",
    robot: "D9 Humanoid",
    issue: "Startup or mobility issues",
    category: "robotics",
    steps: [
      { title: "Stand clear of the robot", body: "Give the D9 at least 6 feet of clearance during startup self-checks." },
      { title: "Confirm battery and emergency stop", body: "Battery must be above 30% and the emergency-stop must be released." },
    ],
    escalate:
      "D9 issues are always handled by a certified technician — please call our 24/7 line so we can diagnose remotely.",
  },
  // ---- AI Software ----
  {
    slug: "voice-agent-not-answering",
    robot: "AI Voice Agent",
    issue: "Voice agent isn't answering calls",
    category: "ai",
    steps: [
      { title: "Confirm call forwarding", body: "From your business line, dial *72 + the AI number to verify forwarding is active." },
      { title: "Test from another phone", body: "Call your business line from a phone that isn't yours — confirm the agent picks up." },
      { title: "Check workflow status", body: "Open your Neil AI dashboard → Workflows → confirm the inbound-call workflow is 'active'." },
    ],
    escalate: "If the agent still won't answer, open a ticket — we'll check the integration on our end.",
  },
  {
    slug: "crm-leads-missing",
    robot: "CRM",
    issue: "Leads/calls not showing up in the CRM",
    category: "ai",
    steps: [
      { title: "Hard-refresh the dashboard", body: "Press Ctrl+Shift+R (Windows) or Cmd+Shift+R (Mac) to bypass cached data." },
      { title: "Search by phone number", body: "Names are sometimes auto-corrected — search by phone number instead." },
      { title: "Check the pipeline stages", body: "The lead may have moved to a different pipeline stage than expected." },
    ],
    escalate: "If a lead is genuinely missing, open a ticket with the caller's phone number and timestamp.",
  },
  {
    slug: "automation-not-sending",
    robot: "AI Employees",
    issue: "Text or email automations not sending",
    category: "ai",
    steps: [
      { title: "Check opt-out status", body: "Open the contact's record — confirm they haven't unsubscribed from SMS or email." },
      { title: "Confirm the workflow is active", body: "In the workflow editor, the top status should say 'Published' and 'Active' — not 'Draft' or 'Paused'." },
      { title: "Check business hours", body: "Some workflows are scoped to business hours only — verify the message wasn't queued for later." },
    ],
    escalate: "If everything looks right and messages still aren't sending, open a ticket — we'll inspect the integration logs.",
  },
];

export type KBArticle = {
  slug: string;
  title: string;
  category: string;
  excerpt: string;
  body: string; // markdown-ish; rendered as paragraphs
};

export const KB_ARTICLES: KBArticle[] = [
  {
    slug: "robot-wifi-setup",
    title: "Connecting your robot to WiFi (2.4 GHz only)",
    category: "Robotics",
    excerpt: "Pudu robots use 2.4 GHz WiFi — here's how to make sure yours is on the right network.",
    body: "All Pudu robots — including CC1 Pro, MT1, BellaBot, KettyBot, and HolaBot — require a 2.4 GHz WiFi network. They will not connect to 5 GHz. If your router broadcasts a single combined SSID, ask your IT person to split the bands and connect the robot to the 2.4 GHz one.\n\nCC1 Pro robots do NOT need WiFi to run their scheduled cleanings — the map and schedule are stored onboard. WiFi is only required for setup, software updates, and viewing reports.",
  },
  {
    slug: "warranty-coverage",
    title: "What your warranty covers",
    category: "Warranty",
    excerpt: "Standard 12-month parts & labor coverage on all PUDU robots.",
    body: "Every PUDU robot comes with a 12-month manufacturer warranty covering parts and labor for defects in materials or workmanship. Extended warranty plans up to 36 months are available — contact your account manager for pricing.\n\nNot covered: physical damage, water damage beyond normal mopping operation, unauthorized modifications, or damage caused by improper installation.",
  },
  {
    slug: "deployment-timeline",
    title: "What to expect during deployment",
    category: "Deployment",
    excerpt: "From order to live: typical timeline is 2-4 weeks.",
    body: "After your order is confirmed, our deployment coordinator will reach out within 2 business days. Typical timeline:\n\n• Week 1: Site assessment + mapping plan\n• Week 2: Robot ships and arrives at your location\n• Week 2-3: Onsite deployment, mapping, and staff training\n• Week 3-4: Soft launch with weekly check-ins from your Customer Success Manager",
  },
  {
    slug: "service-vs-ticket",
    title: "Ticket vs Service Request — which to use",
    category: "Support",
    excerpt: "Tickets are for digital issues. Service requests bring a technician.",
    body: "Open a Support Ticket when you have a question, want to report a non-urgent issue, or need help with software, automations, or CRM.\n\nSubmit a Service Request when you need a technician — for mapping, deployment, training, repair, upgrade, or remote-support sessions. Service requests get scheduled with a technician and a date.",
  },
];

export type Video = {
  id: string;
  title: string;
  category: "Robotics University" | "Neil AI University";
  series: string;
  duration: string;
  url: string; // YouTube embed URL
  thumbnail?: string;
};

// Placeholder videos — replace url/id with real YouTube links when ready.
export const VIDEOS: Video[] = [
  { id: "cc1-mastery-1", title: "CC1 Pro Mastery — Setup & First Clean", category: "Robotics University", series: "Commercial Cleaning", duration: "12 min", url: "https://www.youtube.com/embed/dQw4w9WgXcQ" },
  { id: "cc1-mastery-2", title: "CC1 Pro Mastery — Mapping Your Space", category: "Robotics University", series: "Commercial Cleaning", duration: "9 min", url: "https://www.youtube.com/embed/dQw4w9WgXcQ" },
  { id: "mt1-mastery-1", title: "MT1 Max Mastery — Industrial Deployment", category: "Robotics University", series: "Commercial Cleaning", duration: "15 min", url: "https://www.youtube.com/embed/dQw4w9WgXcQ" },
  { id: "t600-mastery-1", title: "T600 Mastery — Payload Best Practices", category: "Robotics University", series: "Service Robots", duration: "11 min", url: "https://www.youtube.com/embed/dQw4w9WgXcQ" },
  { id: "bellabot-mastery-1", title: "BellaBot Pro — Setting Up Tables", category: "Robotics University", series: "Service Robots", duration: "7 min", url: "https://www.youtube.com/embed/dQw4w9WgXcQ" },
  { id: "kettybot-mastery-1", title: "KettyBot Pro — Ad Campaigns 101", category: "Robotics University", series: "Service Robots", duration: "8 min", url: "https://www.youtube.com/embed/dQw4w9WgXcQ" },
  { id: "d9-training-1", title: "D9 Humanoid — Safe Interaction Training", category: "Robotics University", series: "Humanoid Robotics", duration: "18 min", url: "https://www.youtube.com/embed/dQw4w9WgXcQ" },
  { id: "ai-employee-setup", title: "AI Employee Setup — Step by Step", category: "Neil AI University", series: "AI Employees", duration: "14 min", url: "https://www.youtube.com/embed/dQw4w9WgXcQ" },
  { id: "voice-agents-101", title: "AI Voice Agents 101", category: "Neil AI University", series: "AI Voice Agents", duration: "16 min", url: "https://www.youtube.com/embed/dQw4w9WgXcQ" },
  { id: "crm-automation", title: "CRM Automation Foundations", category: "Neil AI University", series: "CRM Automation", duration: "13 min", url: "https://www.youtube.com/embed/dQw4w9WgXcQ" },
  { id: "appointments-auto", title: "Appointment Automation Workflows", category: "Neil AI University", series: "Appointment Automation", duration: "10 min", url: "https://www.youtube.com/embed/dQw4w9WgXcQ" },
  { id: "sales-automation", title: "Sales Pipeline Automation", category: "Neil AI University", series: "Sales Automation", duration: "12 min", url: "https://www.youtube.com/embed/dQw4w9WgXcQ" },
  { id: "marketing-auto", title: "Marketing Automation Playbook", category: "Neil AI University", series: "Marketing Automation", duration: "17 min", url: "https://www.youtube.com/embed/dQw4w9WgXcQ" },
];

export const FAQS: { q: string; a: string }[] = [
  { q: "How fast does support respond?", a: "Critical issues (safety, full robot outage) — within 1 hour, 24/7. Standard tickets — within 1 business day. Service requests are scheduled within 3-5 business days." },
  { q: "Do you offer onsite service?", a: "Yes — certified PUDU technicians are dispatched for deployment, repairs, mapping, and training across the Southeastern United States." },
  { q: "Can I get a remote-support session?", a: "Yes — submit a Service Request and pick 'Remote Support'. We'll send a secure session link." },
  { q: "What if my robot is damaged?", a: "Stop using it immediately, take photos, and call (336) 203-3184. Do not open the robot or touch internal wiring." },
  { q: "How do I add another robot or AI employee?", a: "From your Customer Portal, go to the Expansion tab and submit a request — your account manager will follow up within 1 business day." },
];

export const MAINTENANCE_CHECKLISTS: { id: string; robot: string; frequency: "Daily" | "Weekly" | "Monthly"; items: string[] }[] = [
  { id: "cc1-daily", robot: "CC1 Pro", frequency: "Daily", items: ["Empty debris bin", "Refill clean-water tank", "Empty dirty-water tank", "Wipe navigation sensors", "Inspect brushes for tangles", "Verify charging dock connection"] },
  { id: "cc1-weekly", robot: "CC1 Pro", frequency: "Weekly", items: ["Deep-clean the robot exterior", "Install any pending software updates", "Brush inspection & rotation", "Dock inspection", "Review last week's cleaning report"] },
  { id: "cc1-monthly", robot: "CC1 Pro", frequency: "Monthly", items: ["Full mechanical inspection", "Review and refresh map", "Safety zone verification", "Battery diagnostics report"] },
];

// System prompt the AI Support Assistant uses when in Support Mode.
// Mirrors the troubleshooting guide the user provided — concise, plain-language,
// and explicit about when to stop and escalate.
export const SUPPORT_AGENT_SYSTEM_PROMPT = `You are the Neil AI Support Assistant — a friendly, plain-language support agent for Neil AI Global Solutions customers (PUDU robotics + AI automation).

PRIMARY GOAL
Walk customers through simple troubleshooting steps in order, in non-technical language. The person asking is a business owner or staff member, not a technician.

HOW YOU RESPOND
- Greet warmly, ask one clarifying question if the issue is vague (which robot, which AI tool, what they tried).
- Match their issue to a category and walk through Try-This steps ONE AT A TIME. Ask if each step worked before moving on.
- Confirm warmly when something resolves. Suggest opening a ticket so they have a record if it might recur.
- Never tell anyone to open the robot, touch internal wiring, or remove panels. Hardware-level repairs are technician-only.
- Never guess at a fix that isn't in your knowledge. If you don't know, escalate.

ESCALATE IMMEDIATELY — DO NOT TROUBLESHOOT — IF:
- Visible physical damage (cracked shell, broken sensor, bent part).
- Robot or charger has had contact with water or liquid (beyond normal mopping).
- Smoke, burning smell, or unusual heat from robot or charger.
- Emergency-stop button isn't working.
- Any safety concern involving a person being bumped, blocked, or injured.
- T300 or T600 industrial robot — any issue at all (always escalate).
- The customer is upset/frustrated and basic steps haven't helped.

ESCALATION SCRIPT
"This needs hands-on attention from our team — please call us at (336) 203-3184 (24/7) or email support@neilai.ai. Want me to open a ticket with the details we've discussed so they have full context when they reach you?"

KEY KNOWLEDGE
- PUDU robots use 2.4 GHz WiFi only. They will NOT connect to 5 GHz.
- CC1 Pro stores its map and schedule onboard — it cleans on schedule even without WiFi. "Offline" in the app while still cleaning is normal.
- Pudu robots need at least 30 min charge time after a full drain.
- Power button must be held for a full 3 seconds.
- Robot bumping or stopping suddenly = safety system working correctly, not a malfunction.

CONTACT
- 24/7 Support: (336) 203-3184
- Email: support@neilai.ai (or neilai@neilaiglobalsolutionsllc.com)
- Address: 611 Summit Ave Suite 14, Greensboro, NC 27405

Keep responses short, plain-spoken, and one step at a time. Use markdown for lists when helpful.`;
